import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { MapPin, Star, ArrowLeft, Calendar, Users, Info, Shield } from 'lucide-react';
import hotelData from '../../data/hotels.json';

const HotelBooking = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get hotel and booking data from route state if available, or fetch it
  const [hotel, setHotel] = useState(location.state?.hotel || null);
  const [selectedRoom, setSelectedRoom] = useState(location.state?.selectedRoom || null);
  const [searchParams] = useState(location.state?.searchParams || {
    dates: { startDate: null, endDate: null },
    guests: { adults: 2, children: 0, rooms: 1 }
  });
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    paymentMethod: 'creditCard',
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: '',
    agreeTerms: false
  });
  
  const [loading, setLoading] = useState(!hotel);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // If hotel is not in state, fetch it by ID
  useEffect(() => {
    if (!hotel && id) {
      setLoading(true);
      // In a real app, this would be an API call
      const foundHotel = hotelData.hotels.find(h => h.id.toString() === id.toString());
      
      if (foundHotel) {
        setHotel(foundHotel);
        
        // Mock room data if not provided
        if (!selectedRoom) {
          setSelectedRoom({
            id: 1,
            name: 'Standard Room',
            price: foundHotel.price,
            capacity: 2,
            bedType: 'Queen'
          });
        }
      } else {
        // Handle hotel not found
        console.error(`Hotel with ID ${id} not found`);
      }
      
      setLoading(false);
    }
  }, [id, hotel, selectedRoom]);
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    if (typeof date === 'string') {
      date = new Date(date);
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  // Calculate total nights
  const calculateNights = () => {
    if (!searchParams.dates.startDate || !searchParams.dates.endDate) return 1;
    
    let startDate = searchParams.dates.startDate;
    let endDate = searchParams.dates.endDate;
    
    if (typeof startDate === 'string') startDate = new Date(startDate);
    if (typeof endDate === 'string') endDate = new Date(endDate);
    
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays || 1;
  };
  
  // Calculate price
  const calculatePrice = () => {
    const basePrice = (selectedRoom?.price || hotel?.price || 0) * calculateNights();
    const taxesAndFees = Math.round(basePrice * 0.15);
    return {
      basePrice,
      taxesAndFees,
      total: basePrice + taxesAndFees
    };
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    
    // Payment validation
    if (formData.paymentMethod === 'creditCard') {
      if (!formData.cardNumber.trim()) newErrors.cardNumber = 'Card number is required';
      else if (!/^\d{16}$/.test(formData.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Card number is invalid';
      }
      
      if (!formData.cardHolder.trim()) newErrors.cardHolder = 'Card holder name is required';
      if (!formData.expiryDate.trim()) newErrors.expiryDate = 'Expiry date is required';
      else if (!/^\d{2}\/\d{2}$/.test(formData.expiryDate)) {
        newErrors.expiryDate = 'Use format MM/YY';
      }
      
      if (!formData.cvv.trim()) newErrors.cvv = 'CVV is required';
      else if (!/^\d{3,4}$/.test(formData.cvv)) newErrors.cvv = 'CVV is invalid';
    }
    
    if (!formData.agreeTerms) newErrors.agreeTerms = 'You must agree to the terms';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setSubmitting(true);
    
    // Simulate API call to create booking
    setTimeout(() => {
      // Generate a random booking ID
      const bookingId = Math.floor(100000 + Math.random() * 900000).toString();
      
      // In a real app, you would send the booking data to your backend
      // and get a booking confirmation response
      
      // Navigate to confirmation page
      navigate(`/hotel/confirmation/${bookingId}`, {
        state: {
          hotel,
          room: selectedRoom,
          booking: {
            id: bookingId,
            checkIn: searchParams.dates.startDate,
            checkOut: searchParams.dates.endDate,
            guests: searchParams.guests,
            guestName: `${formData.firstName} ${formData.lastName}`,
            price: calculatePrice(),
            bookingDate: new Date().toISOString(),
            status: 'confirmed'
          }
        }
      });
      
      setSubmitting(false);
    }, 1500);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading booking page...</p>
      </div>
    );
  }
  
  // Hotel not found state
  if (!hotel) {
    return (
      <div className="container py-5 text-center">
        <h2>Hotel Not Found</h2>
        <p className="text-muted">The hotel you're looking for doesn't exist or has been removed.</p>
        <Link to="/" className="btn btn-primary mt-3">
          <ArrowLeft size={18} className="me-2" />
          Back to Hotels
        </Link>
      </div>
    );
  }
  
  const { basePrice, taxesAndFees, total } = calculatePrice();

  return (
    <div className="container py-4">
      {/* Breadcrumb Navigation */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to={`/hoteldetails/${hotel.id}`}>{hotel.name}</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">Book Now</li>
        </ol>
      </nav>
      
      {/* Mobile Back Button */}
      <div className="d-md-none mb-3">
        <button 
          className="btn btn-outline-secondary" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} className="me-1" />
          Back
        </button>
      </div>
      
      {/* Booking Header */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <h1 className="h3 mb-1">Complete Your Booking</h1>
              <div className="d-flex align-items-center mb-2">
                <h2 className="h5 mb-0 me-2">{hotel.name}</h2>
                <div className="text-warning d-flex me-2">
                  {[...Array(hotel.stars)].map((_, i) => (
                    <Star key={i} size={16} fill="currentColor" />
                  ))}
                </div>
                {hotel.halal_status && (
                  <span className="badge bg-success">{hotel.halal_status}</span>
                )}
              </div>
              <div className="d-flex align-items-center text-muted">
                <MapPin size={16} className="me-1" />
                <small>{`${hotel.location.city}, ${hotel.location.country}`}</small>
              </div>
            </div>
            <div className="col-md-4 text-md-end mt-3 mt-md-0">
              <div className="d-flex flex-column align-items-md-end">
                <div className="text-muted mb-1">
                  {selectedRoom.name} · {calculateNights()} {calculateNights() === 1 ? 'night' : 'nights'}
                </div>
                <div className="h5 mb-0">${total.toFixed(2)}</div>
                <small className="text-muted">Total including taxes</small>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Booking Form */}
      <div className="row">
        <div className="col-lg-8">
          <form onSubmit={handleSubmit}>
            {/* Booking Details Section */}
            <div className="card mb-4">
              <div className="card-header bg-white">
                <h3 className="h5 mb-0">Your Booking Details</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <div className="d-flex align-items-center mb-2">
                      <Calendar size={18} className="text-muted me-2" />
                      <div className="fw-medium">Check-in</div>
                    </div>
                    <div>{formatDate(searchParams.dates.startDate) || 'Not specified'}</div>
                    <small className="text-muted">After 3:00 PM</small>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-2">
                      <Calendar size={18} className="text-muted me-2" />
                      <div className="fw-medium">Check-out</div>
                    </div>
                    <div>{formatDate(searchParams.dates.endDate) || 'Not specified'}</div>
                    <small className="text-muted">Before 12:00 PM</small>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex align-items-center mb-2">
                      <Users size={18} className="text-muted me-2" />
                      <div className="fw-medium">Guests</div>
                    </div>
                    <div>
                      {searchParams.guests.adults} adults, 
                      {searchParams.guests.children > 0 ? ` ${searchParams.guests.children} children, ` : ' '}
                      {searchParams.guests.rooms} room
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Guest Information */}
            <div className="card mb-4">
              <div className="card-header bg-white">
                <h3 className="h5 mb-0">Guest Information</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="firstName" className="form-label">First Name*</label>
                    <input 
                      type="text"
                      className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.firstName && (
                      <div className="invalid-feedback">{errors.firstName}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="lastName" className="form-label">Last Name*</label>
                    <input 
                      type="text"
                      className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.lastName && (
                      <div className="invalid-feedback">{errors.lastName}</div>
                    )}
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <label htmlFor="email" className="form-label">Email Address*</label>
                    <input 
                      type="email"
                      className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.email && (
                      <div className="invalid-feedback">{errors.email}</div>
                    )}
                    <small className="form-text text-muted">We'll send booking confirmation to this email</small>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="phone" className="form-label">Phone Number*</label>
                    <input 
                      type="tel"
                      className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                    {errors.phone && (
                      <div className="invalid-feedback">{errors.phone}</div>
                    )}
                    <small className="form-text text-muted">For booking-related updates</small>
                  </div>
                </div>
                
                <div className="mb-3">
                  <label htmlFor="specialRequests" className="form-label">Special Requests (Optional)</label>
                  <textarea 
                    className="form-control"
                    id="specialRequests"
                    name="specialRequests"
                    rows="3"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    placeholder="Let us know if you have any special requests"
                  ></textarea>
                  <small className="form-text text-muted">We'll do our best to accommodate your requests (subject to availability)</small>
                </div>
              </div>
            </div>
            
            {/* Payment Information */}
            <div className="card mb-4">
              <div className="card-header bg-white">
                <h3 className="h5 mb-0">Payment Information</h3>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Payment Method*</label>
                  <div className="d-flex flex-wrap gap-3">
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio"
                        name="paymentMethod"
                        id="creditCard"
                        value="creditCard"
                        checked={formData.paymentMethod === 'creditCard'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="creditCard">
                        Credit/Debit Card
                      </label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio"
                        name="paymentMethod"
                        id="payAtHotel"
                        value="payAtHotel"
                        checked={formData.paymentMethod === 'payAtHotel'}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="payAtHotel">
                        Pay at Hotel
                      </label>
                    </div>
                  </div>
                </div>
                
                {formData.paymentMethod === 'creditCard' && (
                  <>
                    <div className="row mb-3">
                      <div className="col-12">
                        <label htmlFor="cardNumber" className="form-label">Card Number*</label>
                        <input 
                          type="text"
                          className={`form-control ${errors.cardNumber ? 'is-invalid' : ''}`}
                          id="cardNumber"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          placeholder="1234 5678 9012 3456"
                          maxLength="19"
                        />
                        {errors.cardNumber && (
                          <div className="invalid-feedback">{errors.cardNumber}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="row mb-3">
                      <div className="col-12">
                        <label htmlFor="cardHolder" className="form-label">Cardholder Name*</label>
                        <input 
                          type="text"
                          className={`form-control ${errors.cardHolder ? 'is-invalid' : ''}`}
                          id="cardHolder"
                          name="cardHolder"
                          value={formData.cardHolder}
                          onChange={handleInputChange}
                          placeholder="Name as shown on card"
                        />
                        {errors.cardHolder && (
                          <div className="invalid-feedback">{errors.cardHolder}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label htmlFor="expiryDate" className="form-label">Expiry Date*</label>
                        <input 
                          type="text"
                          className={`form-control ${errors.expiryDate ? 'is-invalid' : ''}`}
                          id="expiryDate"
                          name="expiryDate"
                          value={formData.expiryDate}
                          onChange={handleInputChange}
                          placeholder="MM/YY"
                          maxLength="5"
                        />
                        {errors.expiryDate && (
                          <div className="invalid-feedback">{errors.expiryDate}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="cvv" className="form-label">CVV*</label>
                        <input 
                          type="text"
                          className={`form-control ${errors.cvv ? 'is-invalid' : ''}`}
                          id="cvv"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleInputChange}
                          placeholder="123"
                          maxLength="4"
                        />
                        {errors.cvv && (
                          <div className="invalid-feedback">{errors.cvv}</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="alert alert-info d-flex align-items-center" role="alert">
                      <Shield size={18} className="me-2" />
                      <div>Your payment information is encrypted and securely processed.</div>
                    </div>
                  </>
                )}
                
                {formData.paymentMethod === 'payAtHotel' && (
                  <div className="alert alert-info d-flex align-items-center" role="alert">
                    <Info size={18} className="me-2" />
                    <div>You'll pay directly at the hotel. A valid credit card may still be required to guarantee your booking.</div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Terms and Conditions */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="form-check">
                  <input 
                    className={`form-check-input ${errors.agreeTerms ? 'is-invalid' : ''}`}
                    type="checkbox"
                    id="agreeTerms"
                    name="agreeTerms"
                    checked={formData.agreeTerms}
                    onChange={handleInputChange}
                  />
                 <label className="form-check-label" htmlFor="agreeTerms">
  I agree to the{' '}
  <Link to="/terms-and-conditions">terms and conditions</Link>,{' '}
  <Link to="/cancellation-policy">cancellation policy</Link>, and{' '}
  <Link to="/privacy-policy">privacy policy</Link>*
</label>
                  {errors.agreeTerms && (
                    <div className="invalid-feedback">{errors.agreeTerms}</div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Submit Button */}
            <div className="d-grid">
              <button 
                type="submit" 
                className="btn btn-primary btn-lg"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Processing...
                  </>
                ) : (
                  'Complete Booking'
                )}
              </button>
            </div>
          </form>
        </div>
        
        {/* Right Column: Booking Summary */}
        <div className="col-lg-4 mt-4 mt-lg-0">
          <div className="card shadow-sm sticky-top" style={{ top: '20px', zIndex: 10 }}>
            <div className="card-header bg-white">
              <h3 className="h5 mb-0">Booking Summary</h3>
            </div>
            <div className="card-body">
              {/* Room Details */}
              <div className="d-flex mb-3">
                <div className="flex-shrink-0">
                  <img 
                    src={hotel.image}
                    alt={hotel.name}
                    className="rounded"
                    style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.src = '';
                    }}
                  />
                </div>
                <div className="ms-3">
                  <h4 className="h6 mb-1">{selectedRoom.name}</h4>
                  <div className="text-muted small mb-1">{selectedRoom.bedType} Bed</div>
                  <div className="text-muted small mb-1">Max. {selectedRoom.capacity} guests</div>
                </div>
              </div>
              
              {/* Dates */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <div className="text-muted">Check-in</div>
                  <div>{formatDate(searchParams.dates.startDate) || 'Not specified'}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="text-muted">Check-out</div>
                  <div>{formatDate(searchParams.dates.endDate) || 'Not specified'}</div>
                </div>
              </div>
              
              {/* Guests */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between">
                  <div className="text-muted">Guests</div>
                  <div>
                    {searchParams.guests.adults} adults, 
                    {searchParams.guests.children > 0 ? ` ${searchParams.guests.children} children` : ''}
                  </div>
                </div>
              </div>
              
              {/* Price Details */}
              <div className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between mb-2">
                  <div>{`$${selectedRoom.price} × ${calculateNights()} ${calculateNights() === 1 ? 'night' : 'nights'}`}</div>
                  <div>${basePrice.toFixed(2)}</div>
                </div>
                <div className="d-flex justify-content-between">
                  <div>Taxes and fees</div>
                  <div>${taxesAndFees.toFixed(2)}</div>
                </div>
              </div>
              
              {/* Total */}
              <div className="d-flex justify-content-between align-items-center">
                <div className="fw-bold">Total</div>
                <div className="h4 mb-0">${total.toFixed(2)}</div>
              </div>
              
              {/* Cancellation Policy */}
              <div className="alert alert-light mt-3 mb-0 d-flex align-items-start">
                <Info size={18} className="text-muted me-2 mt-1" />
                <div className="small">
                  <strong>Free cancellation</strong> until 24 hours before check-in. Cancel before {formatDate(searchParams.dates.startDate)} for a full refund.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelBooking;